import { api } from '../client';

// ==========================================
// Types - Storage Locations
// ==========================================

export type StorageLocationType = 'RACK' | 'BIN' | 'FLOOR' | 'OUTDOOR' | 'TANK' | 'PIPE_RACK' | 'CONTAINER';
export type StorageLocationStatus = 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE' | 'BLOCKED';

export interface YardStorageLocation {
  id: string;
  companyId: string;
  locationId: string;
  location: { id: string; name: string; code: string };
  code: string;
  name: string;
  type: StorageLocationType;
  status: StorageLocationStatus;
  zone?: string;
  aisle?: string;
  rack?: string;
  level?: string;
  bin?: string;
  maxWeight?: number;
  maxVolume?: number;
  currentWeight?: number;
  currentVolume?: number;
  dimensions?: { length?: number; width?: number; height?: number };
  specialHandling: string[];
  isActive: boolean;
  _count?: { storedItems: number };
  createdAt: string;
  updatedAt: string;
}

export interface YardStoredItem {
  id: string;
  storageLocationId: string;
  itemId: string;
  item: { id: string; name: string; sku: string; unit: string };
  batchId?: string;
  batch?: { id: string; batchNumber: string };
  quantity: number;
  reservedQty: number;
  serialNumbers: string[];
  storedAt: string;
  lastMovedAt?: string;
  expiryDate?: string;
  notes?: string;
}

// ==========================================
// Types - Putaway
// ==========================================

export type PutawayStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export interface YardPutawayTask {
  id: string;
  taskNumber: string;
  sourceType: string;
  sourceId?: string;
  storageLocationId?: string;
  storageLocation?: { id: string; code: string; name: string };
  suggestedLocationId?: string;
  itemId: string;
  item: { id: string; name: string; sku: string; unit: string };
  batchId?: string;
  quantity: number;
  putawayQty: number;
  status: PutawayStatus;
  assignedToId?: string;
  assignedTo?: { id: string; fullName: string };
  priority: number;
  notes?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ==========================================
// Types - Job Kits
// ==========================================

export type JobKitStatus = 'DRAFT' | 'PENDING_PICK' | 'PICKING' | 'READY' | 'ISSUED' | 'RETURNED' | 'CANCELLED';

export interface YardJobKit {
  id: string;
  kitNumber: string;
  name: string;
  description?: string;
  workOrderId?: string;
  workOrder?: { id: string; workOrderNumber: string; title?: string };
  wellName?: string;
  jobType?: string;
  status: JobKitStatus;
  requiredDate?: string;
  assignedToId?: string;
  assignedTo?: { id: string; fullName: string };
  lines: YardJobKitLine[];
  notes?: string;
  _count?: { lines: number };
  createdAt: string;
  updatedAt: string;
}

export interface YardJobKitLine {
  id: string;
  jobKitId: string;
  itemId: string;
  item: { id: string; name: string; sku: string; unit: string };
  requestedQty: number;
  pickedQty: number;
  issuedQty: number;
  returnedQty: number;
  notes?: string;
}

// ==========================================
// Types - Pick Lists
// ==========================================

export type PickListStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'PARTIALLY_COMPLETED' | 'CANCELLED';

export interface YardPickList {
  id: string;
  pickListNumber: string;
  sourceType: string;
  sourceId: string;
  status: PickListStatus;
  assignedToId?: string;
  assignedTo?: { id: string; fullName: string };
  priority: number;
  lines: YardPickTask[];
  startedAt?: string;
  completedAt?: string;
  notes?: string;
  _count?: { lines: number };
  createdAt: string;
  updatedAt: string;
}

export interface YardPickTask {
  id: string;
  pickListId: string;
  storageLocationId: string;
  storageLocation: { id: string; code: string; name: string; zone?: string };
  itemId: string;
  item: { id: string; name: string; sku: string; unit: string };
  requestedQty: number;
  pickedQty: number;
  status: PickListStatus;
  sequence: number;
  pickedAt?: string;
  notes?: string;
}

// ==========================================
// Types - Pipe Tally
// ==========================================

export type PipeGrade =
  | 'API_5CT_J55' | 'API_5CT_K55' | 'API_5CT_L80' | 'API_5CT_N80'
  | 'API_5CT_C90' | 'API_5CT_T95' | 'API_5CT_P110' | 'API_5CT_Q125' | 'OTHER';

export type PipeCondition = 'NEW' | 'PREMIUM' | 'CLASS_2' | 'CLASS_3' | 'SCRAP';

export interface YardPipeTally {
  id: string;
  companyId: string;
  locationId: string;
  location: { id: string; name: string; code: string };
  tallyNumber: string;
  pipeType: string;
  outerDiameter: number;
  wallThickness: number;
  weight: number;
  grade: PipeGrade;
  connection?: string;
  range?: string;
  condition: PipeCondition;
  joints: number;
  totalFootage: number;
  avgJointLength?: number;
  heatNumber?: string;
  millName?: string;
  certificateId?: string;
  storageLocation?: string;
  status: string;
  lastTalliedAt?: string;
  talliedById?: string;
  talliedBy?: { id: string; fullName: string };
  jointsDetail: YardPipeJoint[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface YardPipeJoint {
  id: string;
  pipeTallyId: string;
  jointNumber: number;
  length: number;
  serialNumber?: string;
  condition: PipeCondition;
  inspectionDate?: string;
  notes?: string;
}

// ==========================================
// Types - Dashboard & Search
// ==========================================

export interface YardDashboard {
  stats: {
    storage: {
      totalLocations: number;
      byStatus: Record<string, number>;
      byType: Record<string, number>;
      occupancy: { storedItems: number; totalQuantity: number };
    };
    putaway: {
      pending: number;
      inProgress: number;
      completedToday: number;
      total: number;
    };
    jobKits: {
      draft: number;
      pendingPick: number;
      picking: number;
      ready: number;
      issued: number;
      total: number;
    };
    picking: {
      pending: number;
      inProgress: number;
      completedToday: number;
      total: number;
    };
    pipeTally: {
      totalTallies: number;
      inStock: number;
      reserved: number;
      issued: number;
      totalJoints: number;
      totalFootage: number;
    };
  };
  recentActivity: {
    putaways: YardPutawayTask[];
    pickLists: YardPickList[];
    jobKits: YardJobKit[];
  };
}

export interface AssetSearchResult {
  type: string;
  id: string;
  name: string;
  identifier: string;
  location?: string;
  details: Record<string, unknown>;
}

// ==========================================
// API Functions - Dashboard & Search
// ==========================================

const yardCoreApi = {
  // Dashboard
  getDashboard: async (): Promise<YardDashboard> => {
    const response = await api.get('/api/yard/dashboard');
    return response.data;
  },

  // Asset Search
  searchAssets: async (query: string): Promise<{ results: AssetSearchResult[]; totalResults: number; query: string }> => {
    const response = await api.get('/api/yard/search', { params: { q: query } });
    return response.data;
  },
};

// ==========================================
// API Functions - Storage Locations
// ==========================================

export const storageLocationsApi = {
  getAll: async (params?: {
    locationId?: string;
    type?: StorageLocationType;
    status?: StorageLocationStatus;
    zone?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/api/yard/storage-locations', { params });
    return response.data;
  },

  getById: async (id: string): Promise<YardStorageLocation> => {
    const response = await api.get(`/api/yard/storage-locations/${id}`);
    return response.data;
  },

  create: async (data: {
    locationId: string;
    code: string;
    name: string;
    type: StorageLocationType;
    zone?: string;
    aisle?: string;
    rack?: string;
    level?: string;
    bin?: string;
    maxWeight?: number;
    maxVolume?: number;
    dimensions?: { length?: number; width?: number; height?: number };
    specialHandling?: string[];
  }): Promise<YardStorageLocation> => {
    const response = await api.post('/api/yard/storage-locations', data);
    return response.data;
  },

  update: async (id: string, data: Partial<YardStorageLocation>): Promise<YardStorageLocation> => {
    const response = await api.patch(`/api/yard/storage-locations/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/yard/storage-locations/${id}`);
    return response.data;
  },

  getContents: async (id: string): Promise<{
    storageLocation: { id: string; code: string; name: string; status: string };
    items: YardStoredItem[];
    totalItems: number;
    totalQuantity: number;
  }> => {
    const response = await api.get(`/api/yard/storage-locations/${id}/contents`);
    return response.data;
  },

  storeItem: async (id: string, data: {
    itemId: string;
    quantity: number;
    batchId?: string;
    serialNumbers?: string[];
    expiryDate?: string;
    notes?: string;
  }): Promise<YardStoredItem> => {
    const response = await api.post(`/api/yard/storage-locations/${id}/items`, data);
    return response.data;
  },

  removeItem: async (id: string, itemId: string, quantity: number) => {
    const response = await api.delete(`/api/yard/storage-locations/${id}/items/${itemId}`, {
      params: { quantity },
    });
    return response.data;
  },

  getStats: async (locationId?: string) => {
    const response = await api.get('/api/yard/storage-locations/stats', {
      params: { locationId },
    });
    return response.data;
  },

  suggestLocation: async (itemId: string, locationId?: string) => {
    const response = await api.post('/api/yard/storage-locations/suggest', { itemId, locationId });
    return response.data;
  },
};

// ==========================================
// API Functions - Putaway
// ==========================================

export const putawayApi = {
  getAll: async (params?: {
    status?: PutawayStatus;
    assignedToId?: string;
    sourceType?: string;
    itemId?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/api/yard/putaway', { params });
    return response.data;
  },

  getById: async (id: string): Promise<YardPutawayTask> => {
    const response = await api.get(`/api/yard/putaway/${id}`);
    return response.data;
  },

  create: async (data: {
    sourceType: string;
    sourceId?: string;
    itemId: string;
    batchId?: string;
    quantity: number;
    suggestedLocationId?: string;
    assignedToId?: string;
    priority?: number;
    notes?: string;
  }): Promise<YardPutawayTask> => {
    const response = await api.post('/api/yard/putaway', data);
    return response.data;
  },

  start: async (id: string): Promise<YardPutawayTask> => {
    const response = await api.patch(`/api/yard/putaway/${id}/start`);
    return response.data;
  },

  complete: async (id: string, data: {
    storageLocationId: string;
    putawayQty: number;
    notes?: string;
  }): Promise<YardPutawayTask> => {
    const response = await api.patch(`/api/yard/putaway/${id}/complete`, data);
    return response.data;
  },

  assign: async (id: string, assignedToId: string): Promise<YardPutawayTask> => {
    const response = await api.patch(`/api/yard/putaway/${id}/assign`, { assignedToId });
    return response.data;
  },

  cancel: async (id: string): Promise<YardPutawayTask> => {
    const response = await api.delete(`/api/yard/putaway/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/yard/putaway/stats');
    return response.data;
  },
};

// ==========================================
// API Functions - Job Kits
// ==========================================

export const jobKitsApi = {
  getAll: async (params?: {
    status?: JobKitStatus;
    workOrderId?: string;
    assignedToId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/api/yard/job-kits', { params });
    return response.data;
  },

  getById: async (id: string): Promise<YardJobKit> => {
    const response = await api.get(`/api/yard/job-kits/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    description?: string;
    workOrderId?: string;
    wellName?: string;
    jobType?: string;
    requiredDate?: string;
    assignedToId?: string;
    lines?: { itemId: string; requestedQty: number; notes?: string }[];
    notes?: string;
  }): Promise<YardJobKit> => {
    const response = await api.post('/api/yard/job-kits', data);
    return response.data;
  },

  update: async (id: string, data: Partial<YardJobKit>): Promise<YardJobKit> => {
    const response = await api.patch(`/api/yard/job-kits/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/yard/job-kits/${id}`);
    return response.data;
  },

  addLine: async (id: string, data: {
    itemId: string;
    requestedQty: number;
    notes?: string;
  }): Promise<YardJobKitLine> => {
    const response = await api.post(`/api/yard/job-kits/${id}/lines`, data);
    return response.data;
  },

  removeLine: async (id: string, lineId: string) => {
    const response = await api.delete(`/api/yard/job-kits/${id}/lines/${lineId}`);
    return response.data;
  },

  updateLine: async (id: string, lineId: string, requestedQty: number) => {
    const response = await api.patch(`/api/yard/job-kits/${id}/lines/${lineId}`, { requestedQty });
    return response.data;
  },

  submitForPicking: async (id: string): Promise<YardJobKit> => {
    const response = await api.patch(`/api/yard/job-kits/${id}/submit`);
    return response.data;
  },

  startPicking: async (id: string): Promise<YardJobKit> => {
    const response = await api.patch(`/api/yard/job-kits/${id}/start-picking`);
    return response.data;
  },

  markReady: async (id: string): Promise<YardJobKit> => {
    const response = await api.patch(`/api/yard/job-kits/${id}/ready`);
    return response.data;
  },

  issue: async (id: string, notes?: string): Promise<YardJobKit> => {
    const response = await api.post(`/api/yard/job-kits/${id}/issue`, { notes });
    return response.data;
  },

  returnItems: async (id: string, items: { lineId: string; returnedQty: number; notes?: string }[], notes?: string): Promise<YardJobKit> => {
    const response = await api.post(`/api/yard/job-kits/${id}/return`, { items, notes });
    return response.data;
  },

  cancel: async (id: string): Promise<YardJobKit> => {
    const response = await api.patch(`/api/yard/job-kits/${id}/cancel`);
    return response.data;
  },

  generatePickList: async (id: string, assignedToId?: string): Promise<YardPickList> => {
    const response = await api.post(`/api/yard/job-kits/${id}/generate-pick-list`, { assignedToId });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/yard/job-kits/stats');
    return response.data;
  },
};

// ==========================================
// API Functions - Picking
// ==========================================

export const pickingApi = {
  getAll: async (params?: {
    status?: PickListStatus;
    sourceType?: string;
    assignedToId?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/api/yard/picking', { params });
    return response.data;
  },

  getById: async (id: string): Promise<YardPickList> => {
    const response = await api.get(`/api/yard/picking/${id}`);
    return response.data;
  },

  start: async (id: string): Promise<YardPickList> => {
    const response = await api.patch(`/api/yard/picking/${id}/start`);
    return response.data;
  },

  completeTask: async (pickListId: string, taskId: string, data: {
    pickedQty: number;
    notes?: string;
  }): Promise<YardPickTask> => {
    const response = await api.patch(`/api/yard/picking/${pickListId}/tasks/${taskId}/complete`, data);
    return response.data;
  },

  complete: async (id: string): Promise<YardPickList> => {
    const response = await api.patch(`/api/yard/picking/${id}/complete`);
    return response.data;
  },

  assign: async (id: string, assignedToId: string): Promise<YardPickList> => {
    const response = await api.patch(`/api/yard/picking/${id}/assign`, { assignedToId });
    return response.data;
  },

  cancel: async (id: string): Promise<YardPickList> => {
    const response = await api.delete(`/api/yard/picking/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/yard/picking/stats');
    return response.data;
  },
};

// ==========================================
// API Functions - Pipe Tally
// ==========================================

export const pipeTallyApi = {
  getAll: async (params?: {
    locationId?: string;
    pipeType?: string;
    grade?: PipeGrade;
    condition?: PipeCondition;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/api/yard/pipe-tally', { params });
    return response.data;
  },

  getById: async (id: string): Promise<YardPipeTally> => {
    const response = await api.get(`/api/yard/pipe-tally/${id}`);
    return response.data;
  },

  create: async (data: {
    locationId: string;
    pipeType: string;
    outerDiameter: number;
    wallThickness: number;
    weight: number;
    grade: PipeGrade;
    connection?: string;
    range?: string;
    condition?: PipeCondition;
    heatNumber?: string;
    millName?: string;
    certificateId?: string;
    storageLocation?: string;
    notes?: string;
  }): Promise<YardPipeTally> => {
    const response = await api.post('/api/yard/pipe-tally', data);
    return response.data;
  },

  update: async (id: string, data: Partial<YardPipeTally>): Promise<YardPipeTally> => {
    const response = await api.patch(`/api/yard/pipe-tally/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/yard/pipe-tally/${id}`);
    return response.data;
  },

  addJoints: async (id: string, joints: {
    jointNumber?: number;
    length: number;
    serialNumber?: string;
    condition: PipeCondition;
    inspectionDate?: string;
    notes?: string;
  }[]): Promise<YardPipeTally> => {
    const response = await api.post(`/api/yard/pipe-tally/${id}/joints`, { joints });
    return response.data;
  },

  removeJoint: async (id: string, jointId: string) => {
    const response = await api.delete(`/api/yard/pipe-tally/${id}/joints/${jointId}`);
    return response.data;
  },

  updateJoint: async (id: string, jointId: string, data: {
    length?: number;
    condition?: PipeCondition;
    notes?: string;
  }) => {
    const response = await api.patch(`/api/yard/pipe-tally/${id}/joints/${jointId}`, data);
    return response.data;
  },

  performTally: async (id: string, data: {
    joints: number;
    totalFootage: number;
    avgJointLength?: number;
    notes?: string;
  }): Promise<YardPipeTally> => {
    const response = await api.post(`/api/yard/pipe-tally/${id}/tally`, data);
    return response.data;
  },

  reserve: async (id: string): Promise<YardPipeTally> => {
    const response = await api.patch(`/api/yard/pipe-tally/${id}/reserve`);
    return response.data;
  },

  issue: async (id: string): Promise<YardPipeTally> => {
    const response = await api.patch(`/api/yard/pipe-tally/${id}/issue`);
    return response.data;
  },

  returnPipe: async (id: string, condition: PipeCondition, notes?: string): Promise<YardPipeTally> => {
    const response = await api.patch(`/api/yard/pipe-tally/${id}/return`, { condition, notes });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/yard/pipe-tally/stats');
    return response.data;
  },

  getSummary: async (locationId?: string) => {
    const response = await api.get('/api/yard/pipe-tally/summary', {
      params: { locationId },
    });
    return response.data;
  },
};

// ==========================================
// Helper Functions
// ==========================================

export const getStorageLocationStatusLabel = (status: StorageLocationStatus): string => {
  const labels: Record<StorageLocationStatus, string> = {
    AVAILABLE: 'Available',
    OCCUPIED: 'Occupied',
    RESERVED: 'Reserved',
    MAINTENANCE: 'Maintenance',
    BLOCKED: 'Blocked',
  };
  return labels[status] || status;
};

export const getJobKitStatusLabel = (status: JobKitStatus): string => {
  const labels: Record<JobKitStatus, string> = {
    DRAFT: 'Draft',
    PENDING_PICK: 'Pending Pick',
    PICKING: 'Picking',
    READY: 'Ready',
    ISSUED: 'Issued',
    RETURNED: 'Returned',
    CANCELLED: 'Cancelled',
  };
  return labels[status] || status;
};

export const getPipeGradeLabel = (grade: PipeGrade): string => {
  const labels: Record<PipeGrade, string> = {
    API_5CT_J55: 'J-55',
    API_5CT_K55: 'K-55',
    API_5CT_L80: 'L-80',
    API_5CT_N80: 'N-80',
    API_5CT_C90: 'C-90',
    API_5CT_T95: 'T-95',
    API_5CT_P110: 'P-110',
    API_5CT_Q125: 'Q-125',
    OTHER: 'Other',
  };
  return labels[grade] || grade;
};

export const getPipeConditionLabel = (condition: PipeCondition): string => {
  const labels: Record<PipeCondition, string> = {
    NEW: 'New',
    PREMIUM: 'Premium',
    CLASS_2: 'Class 2',
    CLASS_3: 'Class 3',
    SCRAP: 'Scrap',
  };
  return labels[condition] || condition;
};

// ==========================================
// Unified Yard API (combines all sub-modules)
// ==========================================

export const yardApi = {
  // Direct methods
  ...yardCoreApi,

  // Nested modules
  storageLocations: storageLocationsApi,
  putaway: putawayApi,
  jobKits: jobKitsApi,
  picking: pickingApi,
  pipeTally: pipeTallyApi,
};

// Export type for asset search result
export type YardAssetSearchResult = AssetSearchResult;
